import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import Tabs from "@/components/base/Tabs";
import { Input, Textarea } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { createSchool, updateSchool, type CreatedAdmin } from "@/services/platformService";
import PlanPicker from "@/components/feature/PlanPicker";
import { CYCLE_SCOPE_LABEL } from "@/utils/cycleScope";
import { PLAN_LABEL, PLAN_MAX_CYCLES, subscriptionState } from "@/utils/plans";
import type { CycleCode, School, SchoolPlan } from "@/types";

const CYCLE_CODES: CycleCode[] = ["PRIMAIRE", "SECONDAIRE"];

interface SchoolFormModalProps {
  open: boolean;
  onClose: () => void;
  school: School | null;
  /** Après enregistrement ; à la création, l'administrateur créé (mot de passe provisoire) le cas échéant. */
  onSaved: (school: School, admin: CreatedAdmin | null) => void;
}

interface FormState {
  name: string;
  shortName: string;
  code: string;
  slogan: string;
  address: string;
  city: string;
  country: string;
  phone: string;
  email: string;
  cycles: CycleCode[];
  isActive: boolean;
  plan: SchoolPlan;
  /** Dernier jour de l'abonnement ; vide : sans échéance. */
  subscriptionEndsAt: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
}

const emptyForm: FormState = {
  name: "",
  shortName: "",
  code: "",
  slogan: "",
  address: "",
  city: "Conakry",
  country: "Guinée",
  phone: "",
  email: "",
  cycles: ["PRIMAIRE"],
  isActive: true,
  plan: "ETABLISSEMENT",
  subscriptionEndsAt: "",
  adminFirstName: "",
  adminLastName: "",
  adminEmail: "",
};

const tabs = [
  { id: "identite", label: "Identité", icon: "ri-bank-line" },
  { id: "coordonnees", label: "Coordonnées", icon: "ri-map-pin-line" },
  { id: "scolarite", label: "Scolarité", icon: "ri-calendar-check-line" },
  { id: "abonnement", label: "Abonnement", icon: "ri-vip-crown-line" },
];

const createTabs = [...tabs, { id: "admin", label: "Administrateur", icon: "ri-user-star-line" }];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_RE = /^[A-Z0-9-]{2,20}$/;

/**
 * Ouverture (super-administrateur) ou configuration d'une école. Le code et les cycles sont fixés à
 * l'ouverture ; l'année scolaire en cours est créée automatiquement. Le logo relève de l'école
 * (Paramètres), la console ne voyant aucune donnée interne.
 */
export default function SchoolFormModal({ open, onClose, school, onSaved }: SchoolFormModalProps) {
  const { notify } = useToast();
  const isEdit = school !== null;
  const [tab, setTab] = useState("identite");
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTab("identite");
    setErrors({});
    if (school) {
      setForm({
        name: school.name,
        shortName: school.shortName,
        code: school.code,
        slogan: school.slogan,
        address: school.address,
        city: school.city,
        country: school.country,
        phone: school.phone,
        email: school.email,
        cycles: [...school.cycles],
        isActive: school.isActive,
        plan: school.plan,
        subscriptionEndsAt: school.subscriptionEndsAt ?? "",
        adminFirstName: "",
        adminLastName: "",
        adminEmail: "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, school]);

  const set = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const toggleCycle = (cycle: CycleCode) => {
    setForm((prev) => ({
      ...prev,
      cycles: prev.cycles.includes(cycle)
        ? prev.cycles.filter((c) => c !== cycle)
        : [...prev.cycles, cycle],
    }));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = "Le nom de l'école est obligatoire.";
    if (!form.shortName.trim()) next.shortName = "Le nom court est obligatoire.";
    if (!isEdit && !CODE_RE.test(form.code.trim()))
      next.code = "Code de 2 à 20 caractères : lettres, chiffres ou tirets.";
    if (!form.city.trim()) next.city = "La ville est obligatoire.";
    if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) next.email = "Format d'email invalide.";
    if (form.cycles.length === 0) next.cycles = "Sélectionnez au moins un cycle.";
    const maxCycles = PLAN_MAX_CYCLES[form.plan];
    if (maxCycles !== null && form.cycles.length > maxCycles)
      next.plan = `Le forfait ${PLAN_LABEL[form.plan]} comprend un seul cycle.`;
    const admin = [form.adminFirstName, form.adminLastName, form.adminEmail].map((v) => v.trim());
    if (!isEdit && admin.some(Boolean)) {
      if (!admin[0]) next.adminFirstName = "Le prénom est obligatoire.";
      if (!admin[1]) next.adminLastName = "Le nom est obligatoire.";
      if (!EMAIL_RE.test(admin[2])) next.adminEmail = "Email invalide.";
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      if (next.name || next.shortName || next.code) setTab("identite");
      else if (next.email || next.city) setTab("coordonnees");
      else if (next.cycles) setTab("scolarite");
      else if (next.plan) setTab("abonnement");
      else setTab("admin");
    }
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      notify("Corrigez les champs en surbrillance.", "error");
      return;
    }
    setSaving(true);
    try {
      const fields = {
        name: form.name.trim(),
        shortName: form.shortName.trim(),
        city: form.city.trim(),
        country: form.country.trim(),
        address: form.address.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        slogan: form.slogan.trim(),
        plan: form.plan,
        subscriptionEndsAt: form.subscriptionEndsAt,
      };
      if (isEdit && school) {
        const saved = await updateSchool(school.id, { ...fields, isActive: form.isActive });
        notify(`École « ${form.shortName} » configurée.`);
        onSaved(saved, null);
      } else {
        const { school: created, admin } = await createSchool({
          ...fields,
          code: form.code,
          cycles: form.cycles,
          admin: form.adminEmail.trim()
            ? {
                firstName: form.adminFirstName.trim(),
                lastName: form.adminLastName.trim(),
                email: form.adminEmail.trim(),
              }
            : undefined,
        });
        notify(`École « ${form.shortName} » ouverte sur la plateforme.`);
        onSaved(created, admin);
      }
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Une erreur est survenue lors de l'enregistrement.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Configurer l'école" : "Ouvrir une école"}
      subtitle={isEdit ? school?.name : "Créez un nouvel établissement sur la plateforme"}
      icon="ri-building-2-line"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-save-3-line" onClick={handleSave} disabled={saving}>
            {saving ? "Enregistrement..." : isEdit ? "Enregistrer la configuration" : "Ouvrir l'école"}
          </Button>
        </>
      }
    >
      <div className="mb-5">
        <Tabs tabs={isEdit ? tabs : createTabs} active={tab} onChange={setTab} />
      </div>

      {tab === "identite" && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Nom de l'école"
            required
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            hint={errors.name}
            wrapperClass="sm:col-span-2"
          />
          <Input
            label="Nom court"
            required
            value={form.shortName}
            onChange={(e) => set({ shortName: e.target.value })}
            hint={errors.shortName ?? "Affiché dans les espaces compacts."}
          />
          <Input
            label="Code / sigle administratif"
            required
            value={form.code}
            disabled={isEdit}
            onChange={(e) => set({ code: e.target.value.toUpperCase() })}
            hint={errors.code ?? (isEdit ? "Fixé à l'ouverture de l'école." : "Unique, ex. LG ou ESP-KINDIA.")}
          />
          <Textarea
            label="Slogan / devise"
            rows={2}
            value={form.slogan}
            onChange={(e) => set({ slogan: e.target.value })}
            wrapperClass="sm:col-span-2"
          />
        </div>
      )}

      {tab === "coordonnees" && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Adresse"
            value={form.address}
            onChange={(e) => set({ address: e.target.value })}
            wrapperClass="sm:col-span-2"
          />
          <Input label="Ville" required value={form.city} onChange={(e) => set({ city: e.target.value })} hint={errors.city} />
          <Input label="Pays" value={form.country} onChange={(e) => set({ country: e.target.value })} />
          <Input label="Téléphone" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => set({ email: e.target.value })}
            hint={errors.email}
          />
        </div>
      )}

      {tab === "scolarite" && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-1.5 text-xs font-medium text-foreground-700">Année scolaire</p>
              <p className="flex h-[38px] items-center rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-600">
                {isEdit ? school?.schoolYear || "—" : "Année en cours, créée automatiquement"}
              </p>
            </div>
            {isEdit && (
              <div>
                <p className="mb-1.5 text-xs font-medium text-foreground-700">Statut de l'école</p>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.isActive}
                  onClick={() => set({ isActive: !form.isActive })}
                  className={`flex h-[38px] w-full items-center justify-between rounded-md border px-3 text-sm transition-colors cursor-pointer ${
                    form.isActive
                      ? "border-primary-300 bg-primary-50 text-primary-800"
                      : "border-background-300 bg-background-100 text-foreground-600"
                  }`}
                >
                  <span>{form.isActive ? "Active sur la plateforme" : "École inactive"}</span>
                  <span
                    className={`relative h-6 w-11 rounded-full transition-colors ${
                      form.isActive ? "bg-primary-600" : "bg-background-300"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 rounded-full bg-background-50 transition-transform ${
                        form.isActive ? "translate-x-[22px]" : "translate-x-0.5"
                      }`}
                      style={{ height: "18px", width: "18px" }}
                    ></span>
                  </span>
                </button>
                {!form.isActive && (
                  <p className="mt-1 text-xs text-accent-600">
                    Une école inactive ne peut plus se connecter (ses données sont conservées).
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium text-foreground-700">
              Cycles ouverts <span className="text-accent-600">*</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {CYCLE_CODES.map((cycle) => {
                const checked = form.cycles.includes(cycle);
                return (
                  <button
                    key={cycle}
                    type="button"
                    disabled={isEdit}
                    onClick={() => toggleCycle(cycle)}
                    className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                      isEdit ? "cursor-default" : "cursor-pointer"
                    } ${
                      checked
                        ? "border-primary-300 bg-primary-50 text-primary-800"
                        : "border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
                    }`}
                  >
                    <i className={checked ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"}></i>
                    {CYCLE_SCOPE_LABEL[cycle]}
                  </button>
                );
              })}
            </div>
            {errors.cycles && <p className="mt-1 text-xs text-accent-600">{errors.cycles}</p>}
            {isEdit && (
              <p className="mt-1 text-xs text-foreground-400">Cycles fixés à l'ouverture de l'école.</p>
            )}
          </div>
        </div>
      )}

      {tab === "abonnement" && (
        <div className="space-y-5">
          <div>
            <p className="mb-1.5 text-xs font-medium text-foreground-700">Forfait</p>
            <PlanPicker value={form.plan} onChange={(plan) => set({ plan })} />
            {errors.plan && <p className="mt-1 text-xs text-accent-600">{errors.plan}</p>}
            {isEdit && (
              <p className="mt-1 text-xs text-foreground-400">
                Un forfait plus petit n'est accepté que si l'école respecte déjà ses limites (rien n'est supprimé).
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Fin de l'abonnement"
              type="date"
              value={form.subscriptionEndsAt}
              onChange={(e) => set({ subscriptionEndsAt: e.target.value })}
              hint={
                !form.subscriptionEndsAt
                  ? "Vide : sans échéance."
                  : subscriptionState(form.subscriptionEndsAt).expired
                    ? "Date passée : l'école est en consultation seule."
                    : "Dernier jour inclus ; l'école est prévenue 30 jours avant."
              }
            />
            {form.subscriptionEndsAt && (
              <div className="flex items-end">
                <Button variant="outline" icon="ri-close-line" onClick={() => set({ subscriptionEndsAt: "" })}>
                  Sans échéance
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "admin" && !isEdit && (
        <div className="space-y-4">
          <p className="text-xs text-foreground-500">
            Facultatif : le directeur reçoit un compte administrateur avec un mot de passe provisoire,
            affiché une seule fois après l'ouverture de l'école.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Prénom"
              value={form.adminFirstName}
              onChange={(e) => set({ adminFirstName: e.target.value })}
              hint={errors.adminFirstName}
            />
            <Input
              label="Nom"
              value={form.adminLastName}
              onChange={(e) => set({ adminLastName: e.target.value })}
              hint={errors.adminLastName}
            />
            <Input
              label="Email de connexion"
              type="email"
              value={form.adminEmail}
              onChange={(e) => set({ adminEmail: e.target.value })}
              hint={errors.adminEmail}
              wrapperClass="sm:col-span-2"
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
